import * as Sentry from '@sentry/react';

import history from '@/store/history';
import { identifySentryUserFromStorage } from '@/utils/sentry';

const IS_PRODUCTION = import.meta.env.VITE_NODE_ENV === 'production';
const DYNAMIC_IMPORT_FAILURE_PATTERN = /Failed to fetch dynamically imported module|Importing a module script failed/;
const DYNAMIC_IMPORT_FAILURE_FINGERPRINT = ['chunk-load-failure'];

type SentryInitOptions = NonNullable<Parameters<typeof Sentry.init>[0]>;

type SentryBeforeSend = NonNullable<SentryInitOptions['beforeSend']>;

type SentryErrorEvent = Parameters<SentryBeforeSend>[0];

function isDynamicImportFailure(event: SentryErrorEvent): boolean {
	const exceptionValues = event.exception?.values ?? [];

	const result = exceptionValues.some((exceptionValue) =>
		DYNAMIC_IMPORT_FAILURE_PATTERN.test(exceptionValue.value ?? '')
	);

	return result;
}

function normalizeChunkLoadFingerprint(event: SentryErrorEvent): SentryErrorEvent {
	if (isDynamicImportFailure(event)) {
		event.fingerprint = DYNAMIC_IMPORT_FAILURE_FINGERPRINT;
	}

	return event;
}

Sentry.init({
	dsn: import.meta.env.VITE_SENTRY_DSN,
	environment: import.meta.env.VITE_NODE_ENV || 'development',
	enableLogs: true,
	integrations: [
		Sentry.reactRouterV5BrowserTracingIntegration({ history }),
		Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
		Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] })
	],
	tracesSampleRate: IS_PRODUCTION ? 0.1 : 1.0,
	replaysSessionSampleRate: 0.05,
	replaysOnErrorSampleRate: 1.0,
	beforeSend: normalizeChunkLoadFingerprint,
	enabled: !!import.meta.env.VITE_SENTRY_DSN
});

identifySentryUserFromStorage();
